import React, { useState, useEffect, useCallback } from 'react';
import API from '../api';
import { toast } from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
const formatNumber = (n) => {
  if (n == null || isNaN(n)) return '0';
  return Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 });
};

const formatDate = (d) => {
  if (!d) return '-';
  const parts = d.split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return d;
};

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
export default function RevenueAnalysis() {
  // ── State ───
  const [period, setPeriod] = useState('day');
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    return {
      start: yesterday.toISOString().split('T')[0],
      end: today.toISOString().split('T')[0]
    };
  });
  const [comparisonData, setComparisonData] = useState(null);
  const [vehicleTypeData, setVehicleTypeData] = useState(null);
  const [unmatchedData, setUnmatchedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview'); // overview | vehicle | hourly

  // ── Fetch ──
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [compRes, vtRes, unmatchedRes] = await Promise.all([
        API.get('/analysis/revenue-comparison', {
          params: { start_date: dateRange.start, end_date: dateRange.end, period }
        }),
        API.get('/analysis/vehicle-type-summary', {
          params: { start_date: dateRange.start, end_date: dateRange.end }
        }),
        API.get('/analysis/unmatched-events', {
          params: { start_date: dateRange.start, end_date: dateRange.end }
        })
      ]);

      setComparisonData(compRes.data);
      setVehicleTypeData(vtRes.data);
      setUnmatchedData(unmatchedRes.data.data);
      toast.success('Analysis data loaded');
    } catch (err) {
      console.error('Analysis error:', err);
      const msg = err.response?.data?.message || err.message;
      toast.error(`Failed to load analysis: ${msg}`);
    } finally {
      setLoading(false);
    }
  }, [dateRange, period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ── Quick date presets ──
  const setPreset = (key) => {
    const today = new Date();
    let start, end;
    switch (key) {
      case 'today':
        start = end = today.toISOString().split('T')[0];
        setPeriod('day');
        break;
      case 'yesterday': {
        const y = new Date(today); y.setDate(y.getDate() - 1);
        start = end = y.toISOString().split('T')[0];
        setPeriod('day');
        break;
      }
      case 'last7': {
        const d = new Date(today); d.setDate(d.getDate() - 6);
        start = d.toISOString().split('T')[0];
        end = today.toISOString().split('T')[0];
        setPeriod('day');
        break;
      }
      case 'last30': {
        const d = new Date(today); d.setDate(d.getDate() - 29);
        start = d.toISOString().split('T')[0];
        end = today.toISOString().split('T')[0];
        setPeriod('week');
        break;
      }
      case 'thisMonth': {
        start = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        end = today.toISOString().split('T')[0];
        setPeriod('day');
        break;
      }
      case 'lastMonth': {
        const firstDayLast = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastDayLast = new Date(today.getFullYear(), today.getMonth(), 0);
        start = firstDayLast.toISOString().split('T')[0];
        end = lastDayLast.toISOString().split('T')[0];
        setPeriod('day');
        break;
      }
      default:
        return;
    }
    setDateRange({ start, end });
  };

  // ── Export ──
  const exportToExcel = () => {
    if (!comparisonData) return;
    try {
      const wb = XLSX.utils.book_new();

      // Sheet 1: Period comparison
      const compRows = comparisonData.comparison.map((r, i) => ({
        'No.': i + 1,
        'Period': r.period,
        'Vehicles (Events)': r.total_vehicles,
        'Estimated Revenue (LAK)': r.estimated_revenue,
        'Transactions (Actual)': r.total_transactions,
        'Actual Revenue (LAK)': r.actual_revenue,
        'Difference (LAK)': r.difference,
        'Collection Rate (%)': r.collection_rate
      }));
      // Grand Total row
      if (comparisonData.grand_totals) {
        const gt = comparisonData.grand_totals;
        compRows.push({
          'No.': '',
          'Period': 'GRAND TOTAL',
          'Vehicles (Events)': gt.total_vehicles,
          'Estimated Revenue (LAK)': gt.total_estimated_revenue,
          'Transactions (Actual)': gt.total_transactions,
          'Actual Revenue (LAK)': gt.total_actual_revenue,
          'Difference (LAK)': gt.total_difference,
          'Collection Rate (%)': gt.overall_collection_rate
        });
      }
      const ws1 = XLSX.utils.json_to_sheet(compRows);
      XLSX.utils.book_append_sheet(wb, ws1, 'Revenue Comparison');

      // Sheet 2: Vehicle type breakdown
      if (vehicleTypeData?.vehicle_type_comparison) {
        const vtRows = vehicleTypeData.vehicle_type_comparison.map((r, i) => ({
          'No.': i + 1,
          'Vehicle Type': r.vehicle_type,
          'Events Count': r.event_count,
          'Estimated Price (LAK)': r.estimated_price,
          'Transactions Count': r.transaction_count,
          'Actual Revenue (LAK)': r.actual_revenue,
          'Difference (LAK)': r.difference,
          'Collection Rate (%)': r.collection_rate
        }));
        const ws2 = XLSX.utils.json_to_sheet(vtRows);
        XLSX.utils.book_append_sheet(wb, ws2, 'Vehicle Type Breakdown');
      }

      // Sheet 3: Unmatched Events
      if (unmatchedData && unmatchedData.length > 0) {
        const unRows = unmatchedData.map((r, i) => ({
          'No.': i + 1,
          'Date / Time': new Date(r.created_at).toLocaleString(),
          'Plate Number': r.plate_number,
          'Vehicle Type': r.vehicle_type,
          'Gate ID': r.gate_id,
          'Estimated Price (LAK)': r.estimated_price
        }));
        const ws3 = XLSX.utils.json_to_sheet(unRows);
        XLSX.utils.book_append_sheet(wb, ws3, 'Missing Transactions');
      }

      const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, `Revenue_Analysis_${dateRange.start}_to_${dateRange.end}.xlsx`);
      toast.success('Excel exported successfully');
    } catch (err) {
      toast.error('Export failed');
      console.error(err);
    }
  };

  // ── Bar helper (CSS-only bar chart) ──
  const Bar = ({ value, max, color = '#3b82f6', label }) => {
    const pct = max > 0 ? (value / max) * 100 : 0;
    return (
      <div className="flex items-center gap-2 w-full">
        <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-5 relative overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-700 ease-out"
            style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: color }}
          />
        </div>
        <span className="text-xs font-medium w-24 text-right whitespace-nowrap">{label || formatNumber(value)}</span>
      </div>
    );
  };

  // ── Collection Rate Badge ──
  const RateBadge = ({ rate }) => {
    const val = parseFloat(rate) || 0;
    let bg, text;
    if (val >= 90) { bg = 'bg-green-100 dark:bg-green-900'; text = 'text-green-800 dark:text-green-200'; }
    else if (val >= 70) { bg = 'bg-yellow-100 dark:bg-yellow-900'; text = 'text-yellow-800 dark:text-yellow-200'; }
    else if (val > 0) { bg = 'bg-red-100 dark:bg-red-900'; text = 'text-red-800 dark:text-red-200'; }
    else { bg = 'bg-gray-100 dark:bg-gray-700'; text = 'text-gray-600 dark:text-gray-300'; }
    return <span className={`px-2 py-1 rounded-full text-xs font-bold ${bg} ${text}`}>{val}%</span>;
  };

  // ════════════════════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════════════════════
  const gt = comparisonData?.grand_totals;

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-600 rounded-xl p-6 text-white shadow-lg">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">📊 Revenue Analysis</h1>
            <p className="text-indigo-200 text-sm mt-1">
              Compare estimated revenue (barrier_events) vs actual revenue (barrier_transactions)
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={exportToExcel}
              disabled={!comparisonData || loading}
              className="px-4 py-2 bg-white/20 backdrop-blur rounded-lg hover:bg-white/30 transition-colors text-sm font-medium disabled:opacity-50"
            >
              📥 Export Excel
            </button>
            <button
              onClick={fetchData}
              disabled={loading}
              className="px-4 py-2 bg-white/20 backdrop-blur rounded-lg hover:bg-white/30 transition-colors text-sm font-medium disabled:opacity-50"
            >
              🔄 Refresh
            </button>
          </div>
        </div>
      </div>

      {/* ── Filters ── */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-5 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col lg:flex-row lg:items-end gap-4">
          {/* Date inputs */}
          <div className="flex gap-3 items-end">
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1 font-medium">Start Date</label>
              <input
                type="date"
                value={dateRange.start}
                onChange={e => setDateRange(p => ({ ...p, start: e.target.value }))}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1 font-medium">End Date</label>
              <input
                type="date"
                value={dateRange.end}
                onChange={e => setDateRange(p => ({ ...p, end: e.target.value }))}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Period selector */}
          <div>
            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1 font-medium">Group By</label>
            <div className="flex rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600">
              {['day', 'week', 'month'].map(p => (
                <button
                  key={p}
                  onClick={() => setPeriod(p)}
                  className={`px-4 py-2 text-sm font-medium capitalize transition-colors ${
                    period === p
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          {/* Quick presets */}
          <div className="flex flex-wrap gap-2">
            {[
              { key: 'today', label: 'Today' },
              { key: 'yesterday', label: 'Yesterday' },
              { key: 'last7', label: '7 Days' },
              { key: 'last30', label: '30 Days' },
              { key: 'thisMonth', label: 'This Month' },
              { key: 'lastMonth', label: 'Last Month' },
            ].map(p => (
              <button
                key={p.key}
                onClick={() => setPreset(p.key)}
                className="px-3 py-1.5 text-xs font-medium border border-gray-300 dark:border-gray-600 rounded-full 
                  text-gray-600 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 
                  hover:border-indigo-400 dark:hover:border-indigo-500 transition-colors"
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Loading ── */}
      {loading && (
        <div className="flex justify-center py-12">
          <div className="flex flex-col items-center gap-3">
            <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
            <span className="text-gray-500 dark:text-gray-400 text-sm">Loading analysis data...</span>
          </div>
        </div>
      )}

      {/* ── Summary Cards ── */}
      {gt && !loading && (
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
          <SummaryCard
            icon="🚗" label="Total Vehicles"
            value={formatNumber(gt.total_vehicles)}
            sub="from barrier_events"
            gradient="from-blue-500 to-blue-600"
          />
          <SummaryCard
            icon="📋" label="Total Transactions"
            value={formatNumber(gt.total_transactions)}
            sub="from barrier_transactions"
            gradient="from-emerald-500 to-emerald-600"
          />
          <SummaryCard
            icon="💡" label="Estimated Revenue"
            value={formatNumber(gt.total_estimated_revenue)}
            sub="LAK (calculate_price)"
            gradient="from-amber-500 to-orange-500"
          />
          <SummaryCard
            icon="💰" label="Actual Revenue"
            value={formatNumber(gt.total_actual_revenue)}
            sub="LAK (barrier_transactions)"
            gradient="from-green-500 to-green-600"
          />
          <SummaryCard
            icon={gt.total_difference >= 0 ? '📈' : '📉'}
            label="Difference"
            value={`${gt.total_difference >= 0 ? '+' : ''}${formatNumber(gt.total_difference)}`}
            sub="LAK (actual - estimated)"
            gradient={gt.total_difference >= 0 ? 'from-teal-500 to-cyan-500' : 'from-red-500 to-rose-500'}
          />
          <SummaryCard
            icon="🎯" label="Collection Rate"
            value={`${gt.overall_collection_rate}%`}
            sub="actual / estimated"
            gradient={parseFloat(gt.overall_collection_rate) >= 80 ? 'from-indigo-500 to-purple-500' : 'from-rose-500 to-pink-500'}
          />
        </div>
      )}

      {/* ── Tab Navigation ── */}
      {!loading && comparisonData && (
        <>
          <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-xl w-fit">
            {[
              { key: 'overview', label: '📊 Period Overview', },
              { key: 'vehicle', label: '🚗 Vehicle Types', },
              { key: 'detail', label: '📋 Detailed Breakdown', },
              { key: 'unmatched', label: '⚠️ Missing Transactions', },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab.key
                    ? 'bg-white dark:bg-gray-700 text-indigo-700 dark:text-indigo-300 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* ─── TAB: Period Overview ─── */}
          {activeTab === 'overview' && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700">
              <div className="p-5 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-bold text-gray-800 dark:text-white">
                  📊 Revenue Comparison by {period.charAt(0).toUpperCase() + period.slice(1)}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {formatDate(dateRange.start)} — {formatDate(dateRange.end)} &nbsp;|&nbsp; 
                  {comparisonData.comparison.length} period(s)
                </p>
              </div>

              {/* Visual bar comparison */}
              <div className="p-5 space-y-4">
                {comparisonData.comparison.map((row, idx) => {
                  const maxVal = Math.max(row.estimated_revenue, row.actual_revenue, 1);
                  return (
                    <div key={idx} className="border-b border-gray-100 dark:border-gray-700 pb-4 last:border-0">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-gray-800 dark:text-white text-sm">{formatDate(row.period)}</span>
                        <RateBadge rate={row.collection_rate} />
                      </div>
                      <div className="space-y-1.5">
                        <div className="flex items-center gap-2">
                          <span className="text-xs w-20 text-gray-500 dark:text-gray-400">Estimated</span>
                          <Bar value={row.estimated_revenue} max={maxVal} color="#f59e0b" label={`${formatNumber(row.estimated_revenue)} LAK`} />
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs w-20 text-gray-500 dark:text-gray-400">Actual</span>
                          <Bar value={row.actual_revenue} max={maxVal} color="#10b981" label={`${formatNumber(row.actual_revenue)} LAK`} />
                        </div>
                      </div>
                      <div className="flex gap-6 mt-2 text-xs text-gray-400 dark:text-gray-500">
                        <span>🚗 {formatNumber(row.total_vehicles)} vehicles</span>
                        <span>📜 {formatNumber(row.total_transactions)} transactions</span>
                        <span className={row.difference >= 0 ? 'text-green-500' : 'text-red-500'}>
                          {row.difference >= 0 ? '▲' : '▼'} {formatNumber(Math.abs(row.difference))} LAK
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Grand total footer */}
              {gt && (
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 p-5 rounded-b-xl border-t border-gray-200 dark:border-gray-700">
                  <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-center">
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Vehicles</div>
                      <div className="text-lg font-bold text-gray-800 dark:text-white">{formatNumber(gt.total_vehicles)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Transactions</div>
                      <div className="text-lg font-bold text-gray-800 dark:text-white">{formatNumber(gt.total_transactions)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Estimated</div>
                      <div className="text-lg font-bold text-amber-600 dark:text-amber-400">{formatNumber(gt.total_estimated_revenue)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Actual</div>
                      <div className="text-lg font-bold text-green-600 dark:text-green-400">{formatNumber(gt.total_actual_revenue)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Difference</div>
                      <div className={`text-lg font-bold ${gt.total_difference >= 0 ? 'text-teal-600 dark:text-teal-400' : 'text-red-600 dark:text-red-400'}`}>
                        {gt.total_difference >= 0 ? '+' : ''}{formatNumber(gt.total_difference)}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Rate</div>
                      <div className="text-lg font-bold text-indigo-600 dark:text-indigo-400">{gt.overall_collection_rate}%</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ─── TAB: Vehicle Types ─── */}
          {activeTab === 'vehicle' && vehicleTypeData && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700">
              <div className="p-5 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-bold text-gray-800 dark:text-white">🚗 Revenue by Vehicle Type</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Comparing estimated price (via calculate_price) with actual barrier transaction revenue
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-gray-700/50">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Vehicle Type</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Events</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase">Estimated (LAK)</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Transactions</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-green-600 dark:text-green-400 uppercase">Actual (LAK)</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Diff (LAK)</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Rate</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {vehicleTypeData.vehicle_type_comparison.map((row, idx) => (
                      <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{getVehicleIcon(row.vehicle_type)}</span>
                            <span className="font-medium text-gray-800 dark:text-white text-sm">{row.vehicle_type || 'Unknown'}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right text-sm text-gray-600 dark:text-gray-300">{formatNumber(row.event_count)}</td>
                        <td className="px-4 py-3 text-right text-sm font-semibold text-amber-600 dark:text-amber-400">{formatNumber(row.estimated_price)}</td>
                        <td className="px-4 py-3 text-right text-sm text-gray-600 dark:text-gray-300">{formatNumber(row.transaction_count)}</td>
                        <td className="px-4 py-3 text-right text-sm font-semibold text-green-600 dark:text-green-400">{formatNumber(row.actual_revenue)}</td>
                        <td className={`px-4 py-3 text-right text-sm font-medium ${row.difference >= 0 ? 'text-teal-600 dark:text-teal-400' : 'text-red-500'}`}>
                          {row.difference >= 0 ? '+' : ''}{formatNumber(row.difference)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <RateBadge rate={row.collection_rate} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {/* Totals */}
                  <tfoot>
                    <tr className="bg-indigo-50 dark:bg-indigo-900/20 font-bold">
                      <td className="px-4 py-3 text-sm text-gray-800 dark:text-white">TOTAL</td>
                      <td className="px-4 py-3 text-right text-sm text-gray-800 dark:text-white">
                        {formatNumber(vehicleTypeData.vehicle_type_comparison.reduce((s, r) => s + r.event_count, 0))}
                      </td>
                      <td className="px-4 py-3 text-right text-sm text-amber-700 dark:text-amber-300">
                        {formatNumber(vehicleTypeData.vehicle_type_comparison.reduce((s, r) => s + r.estimated_price, 0))}
                      </td>
                      <td className="px-4 py-3 text-right text-sm text-gray-800 dark:text-white">
                        {formatNumber(vehicleTypeData.vehicle_type_comparison.reduce((s, r) => s + r.transaction_count, 0))}
                      </td>
                      <td className="px-4 py-3 text-right text-sm text-green-700 dark:text-green-300">
                        {formatNumber(vehicleTypeData.vehicle_type_comparison.reduce((s, r) => s + r.actual_revenue, 0))}
                      </td>
                      <td className="px-4 py-3 text-right text-sm text-teal-700 dark:text-teal-300">
                        {formatNumber(vehicleTypeData.vehicle_type_comparison.reduce((s, r) => s + r.difference, 0))}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {(() => {
                          const totEst = vehicleTypeData.vehicle_type_comparison.reduce((s, r) => s + r.estimated_price, 0);
                          const totAct = vehicleTypeData.vehicle_type_comparison.reduce((s, r) => s + r.actual_revenue, 0);
                          const rate = totEst > 0 ? ((totAct / totEst) * 100).toFixed(1) : 0;
                          return <RateBadge rate={rate} />;
                        })()}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Visual Vehicle Type Chart */}
              <div className="p-5 border-t border-gray-200 dark:border-gray-700">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Revenue Distribution by Vehicle Type</h4>
                {vehicleTypeData.vehicle_type_comparison.map((row, idx) => {
                  const maxVal = Math.max(
                    ...vehicleTypeData.vehicle_type_comparison.map(r => Math.max(r.estimated_price, r.actual_revenue)),
                    1
                  );
                  return (
                    <div key={idx} className="mb-3">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm">{getVehicleIcon(row.vehicle_type)}</span>
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-300 w-28 truncate">{row.vehicle_type || 'Unknown'}</span>
                      </div>
                      <div className="space-y-1 pl-8">
                        <Bar value={row.estimated_price} max={maxVal} color="#f59e0b" label={`${formatNumber(row.estimated_price)} LAK`} />
                        <Bar value={row.actual_revenue} max={maxVal} color="#10b981" label={`${formatNumber(row.actual_revenue)} LAK`} />
                      </div>
                    </div>
                  );
                })}
                <div className="flex gap-6 mt-4 text-xs text-gray-500 dark:text-gray-400">
                  <span className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-full bg-amber-500 inline-block" /> Estimated
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-full bg-emerald-500 inline-block" /> Actual
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* ─── TAB: Detailed Breakdown ─── */}
          {activeTab === 'detail' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Estimated by Vehicle Type */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-amber-50 dark:bg-amber-900/10 rounded-t-xl">
                  <h4 className="font-bold text-amber-800 dark:text-amber-300 flex items-center gap-2">
                    💡 Estimated Revenue (barrier_events)
                  </h4>
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                    Using calculate_price(vehicle_type) for entries with status = 'RECEIVED'
                  </p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-gray-700/50">
                        <th className="px-3 py-2 text-left text-xs font-medium">Period</th>
                        <th className="px-3 py-2 text-left text-xs font-medium">Vehicle Type</th>
                        <th className="px-3 py-2 text-right text-xs font-medium">Count</th>
                        <th className="px-3 py-2 text-right text-xs font-medium">Est. Revenue</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {(comparisonData.estimated_by_vehicle_type || []).map((row, idx) => (
                        <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                          <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{formatDate(row.period_label)}</td>
                          <td className="px-3 py-2">
                            <span className="flex items-center gap-1">
                              {getVehicleIcon(row.vehicle_type)}
                              <span className="text-gray-800 dark:text-white">{row.vehicle_type || 'Unknown'}</span>
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right text-gray-600 dark:text-gray-300">{formatNumber(row.vehicle_count)}</td>
                          <td className="px-3 py-2 text-right font-semibold text-amber-600 dark:text-amber-400">
                            {formatNumber(row.estimated_revenue)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Actual by Payment Method */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-green-50 dark:bg-green-900/10 rounded-t-xl">
                  <h4 className="font-bold text-green-800 dark:text-green-300 flex items-center gap-2">
                    💰 Actual Revenue (barrier_transactions)
                  </h4>
                  <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                    Successful transactions grouped by payment method
                  </p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-gray-700/50">
                        <th className="px-3 py-2 text-left text-xs font-medium">Period</th>
                        <th className="px-3 py-2 text-left text-xs font-medium">Payment Method</th>
                        <th className="px-3 py-2 text-right text-xs font-medium">Count</th>
                        <th className="px-3 py-2 text-right text-xs font-medium">Revenue</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {(comparisonData.actual_by_payment_method || []).map((row, idx) => (
                        <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                          <td className="px-3 py-2 text-gray-600 dark:text-gray-300">{formatDate(row.period_label)}</td>
                          <td className="px-3 py-2">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${getPaymentBadge(row.payment_method)}`}>
                              {getPaymentIcon(row.payment_method)} {row.payment_method || 'N/A'}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right text-gray-600 dark:text-gray-300">{formatNumber(row.transaction_count)}</td>
                          <td className="px-3 py-2 text-right font-semibold text-green-600 dark:text-green-400">
                            {formatNumber(row.actual_revenue)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ─── TAB: Missing Transactions (Unmatched Events) ─── */}
          {activeTab === 'unmatched' && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700">
              <div className="p-5 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-rose-50 dark:bg-rose-900/10 rounded-t-xl">
                <div>
                  <h3 className="text-lg font-bold text-rose-800 dark:text-rose-300">⚠️ Missing Transactions</h3>
                  <p className="text-sm text-rose-600 dark:text-rose-400 mt-1">
                    Barrier events that have plate numbers but NO matching transaction on the same day.
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-rose-600 dark:text-rose-400">{unmatchedData?.length || 0}</div>
                  <div className="text-xs text-rose-500 font-medium">unmatched records</div>
                </div>
              </div>
              
              <div className="overflow-x-auto">
                {unmatchedData && unmatchedData.length > 0 ? (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-gray-700/50">
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">#</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Date & Time</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Plate Number</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Vehicle Type</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Gate</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Est. Price (LAK)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {unmatchedData.map((row, idx) => (
                        <tr key={row.id || idx} className="hover:bg-rose-50/50 dark:hover:bg-rose-900/20 transition-colors">
                          <td className="px-4 py-3 text-gray-500">{idx + 1}</td>
                          <td className="px-4 py-3 text-gray-800 dark:text-gray-200 font-medium whitespace-nowrap">
                            {new Date(row.created_at).toLocaleString()}
                          </td>
                          <td className="px-4 py-3">
                            <span className="px-2.5 py-1 bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-200 font-bold border border-yellow-300 dark:border-yellow-700 rounded shadow-sm inline-block">
                              {row.plate_number}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                            {getVehicleIcon(row.vehicle_type)} {row.vehicle_type}
                          </td>
                          <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{row.gate_id}</td>
                          <td className="px-4 py-3 text-right font-semibold text-amber-600 dark:text-amber-400">
                            {formatNumber(row.estimated_price)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gray-50 dark:bg-gray-700/50 font-bold">
                        <td colSpan="5" className="px-4 py-3 text-right text-sm text-gray-600 dark:text-gray-300">
                          Total Estimated Missing Revenue:
                        </td>
                        <td className="px-4 py-3 text-right text-sm text-amber-600 dark:text-amber-400">
                          {formatNumber(unmatchedData.reduce((sum, row) => sum + Number(row.estimated_price || 0), 0))}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                ) : (
                  <div className="p-12 text-center">
                    <div className="text-4xl mb-3">🎉</div>
                    <h4 className="text-lg font-medium text-gray-800 dark:text-gray-200">No missing transactions!</h4>
                    <p className="text-sm text-gray-500 mt-1">All barrier entries with a plate number match a transaction for this period.</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Empty state */}
      {!loading && (!comparisonData || comparisonData.comparison.length === 0) && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-12 text-center border border-gray-200 dark:border-gray-700">
          <div className="text-5xl mb-4">📭</div>
          <h3 className="text-lg font-semibold text-gray-600 dark:text-gray-300">No data found</h3>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
            Try adjusting the date range or check that barrier_events and barrier_transactions have data.
          </p>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────
function SummaryCard({ icon, label, value, sub, gradient }) {
  return (
    <div className={`bg-gradient-to-br ${gradient} rounded-xl p-4 text-white shadow-md hover:shadow-lg transition-shadow`}>
      <div className="flex items-center gap-2 mb-1">
        <span className="text-xl">{icon}</span>
        <span className="text-xs font-medium text-white/80">{label}</span>
      </div>
      <div className="text-xl font-bold">{value}</div>
      <div className="text-xs text-white/70 mt-0.5">{sub}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Vehicle icon helper
// ─────────────────────────────────────────────────────────────────────────────
function getVehicleIcon(type) {
  if (!type) return '🚗';
  const t = type.toLowerCase();
  if (t.includes('truck') || t.includes('lorry')) return '🚛';
  if (t.includes('bus')) return '🚌';
  if (t.includes('van')) return '🚐';
  if (t.includes('motor') || t.includes('bike')) return '🏍️';
  if (t.includes('suv') || t.includes('pickup')) return '🚙';
  if (t.includes('sedan') || t.includes('car')) return '🚗';
  if (t.includes('mini')) return '🚕';
  return '🚗';
}

function getPaymentIcon(method) {
  if (!method) return '💳';
  const m = method.toLowerCase();
  if (m.includes('cash')) return '💵';
  if (m.includes('mifare')) return '💳';
  if (m.includes('obu')) return '📡';
  if (m.includes('qr')) return '📱';
  return '💳';
}

function getPaymentBadge(method) {
  if (!method) return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  const m = method.toLowerCase();
  if (m.includes('cash')) return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300';
  if (m.includes('mifare')) return 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300';
  if (m.includes('obu')) return 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300';
  if (m.includes('qr')) return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300';
  return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
}
